import { DestroyRef, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  type Observable,
  catchError,
  finalize,
  map,
  of,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';

import { I18nService } from '../i18n/i18n.service';
import { LoggerService } from '../logging/logger.service';
import { SESSION_SCOPED_KEYS } from '../storage/storage.keys';
import { StorageService } from '../storage/storage.service';
import { AuthApi } from './auth.api';
import { isTokenExpired } from './auth.mapper';
import type {
  AuthSession,
  AuthToken,
  AuthUser,
  EmailConfirmation,
  LoginCredentials,
  PasswordResetRequest,
  RegistrationDetails,
  RegistrationResult,
  SessionEndReason,
  SetPasswordRequest,
} from './auth.models';
import { AuthStore } from './auth.store';
import { TokenStorageService } from './token-storage.service';

const RENEW_LEAD_MS = 5 * 60_000;

const MAX_TIMEOUT_MS = 2 ** 31 - 1;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly api = inject(AuthApi);
  private readonly store = inject(AuthStore);
  private readonly tokens = inject(TokenStorageService);
  private readonly storage = inject(StorageService);
  private readonly logger = inject(LoggerService);
  private readonly i18n = inject(I18nService);
  private readonly router = inject(Router);

  private renewInFlight: Observable<AuthToken> | null = null;
  private renewTimer: ReturnType<typeof setTimeout> | null = null;

  readonly user = this.store.user;
  readonly status = this.store.status;
  readonly isAuthenticated = this.store.isAuthenticated;
  readonly isInitializing = this.store.isInitializing;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.cancelScheduledRenew());
  }

  login(credentials: LoginCredentials): Observable<AuthSession> {
    return this.api.createSession(credentials).pipe(
      tap((token) => this.tokens.save(token)),
      switchMap((token) =>
        this.api.currentUser().pipe(
          map((user) => ({ user, token }) satisfies AuthSession),
          catchError((error: unknown) => {
            this.tokens.clear();

            return throwError(() => error);
          }),
        ),
      ),
      tap((session) => {
        this.store.setUser(session.user);
        this.i18n.adoptFromProfile(session.user.defaultLanguage);
        this.scheduleRenew(session.token);
      }),
    );
  }

  /**
   * Registering no longer signs anyone in: the backend mails a confirmation
   * link and the result says where it went. An older backend that still
   * answers with a token gets today's behaviour — the session is established.
   */
  register(details: RegistrationDetails): Observable<RegistrationResult> {
    return this.api
      .register(details)
      .pipe(
        switchMap((result) =>
          result.confirmationRequired
            ? of(result)
            : this.establishSession(result.token).pipe(map(() => result)),
        ),
      );
  }

  confirmEmail(token: string): Observable<EmailConfirmation> {
    return this.api.confirmEmail(token);
  }

  resendConfirmation(email: string): Observable<void> {
    return this.api.resendConfirmation(email);
  }

  saveLanguage(language: string): Observable<void> {
    return this.api.setLanguage(language).pipe(
      catchError(() => {
        this.logger.warn('Could not store the language preference.');

        return of(undefined);
      }),
    );
  }

  requestPasswordReset(request: PasswordResetRequest): Observable<void> {
    return this.api.requestPasswordReset(request);
  }

  setPassword(request: SetPasswordRequest): Observable<void> {
    return this.api.setPassword(request);
  }

  restoreSession(): Observable<void> {
    const token = this.tokens.token;

    if (token === null || isTokenExpired(token)) {
      this.discardSession();
      this.store.clear();

      return of(undefined);
    }

    return this.api.currentUser().pipe(
      tap((user) => {
        this.i18n.adoptFromProfile(user.defaultLanguage);
        this.store.setUser(user);
        this.scheduleRenew(token);
      }),
      map(() => undefined),
      catchError((error: unknown) => {
        this.logger.debug('Session restore failed; continuing as anonymous.', error);
        this.discardSession();
        this.store.clear();

        return of(undefined);
      }),
    );
  }

  renew(): Observable<AuthToken> {
    this.renewInFlight ??= this.api.renewSession().pipe(
      tap((token) => {
        this.tokens.save(token);
        this.scheduleRenew(token);
      }),
      catchError((error: unknown) => {
        this.endSession('expired');

        return throwError(() => error);
      }),
      finalize(() => {
        this.renewInFlight = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.renewInFlight;
  }

  switchWorkspace(companyId: string, branchId: string): Observable<AuthUser> {
    return this.api.setWorkspace(companyId, branchId).pipe(
      switchMap(() => this.api.currentUser()),
      tap((user) => this.store.setUser(user)),
    );
  }

  refreshUser(): Observable<AuthUser> {
    return this.api.currentUser().pipe(tap((user) => this.store.setUser(user)));
  }

  logout(): Observable<void> {
    return this.api.destroySession().pipe(
      catchError((error: unknown) => {
        this.logger.warn('Server logout failed; clearing local session anyway.', error);

        return of(undefined);
      }),
      map(() => undefined),
      tap(() => this.endSession('signed-out')),
    );
  }

  endSession(reason: SessionEndReason): void {
    const returnUrl = this.router.url;

    this.discardSession();
    this.store.clear(reason);

    const isOnLogin = returnUrl.startsWith('/login');
    const shouldPreserve = !isOnLogin && returnUrl !== '/' && reason === 'expired';

    void this.router.navigate(['/login'], {
      queryParams: shouldPreserve ? { returnUrl } : {},
      replaceUrl: true,
    });
  }

  private establishSession(token: AuthToken): Observable<AuthSession> {
    this.tokens.save(token);

    return this.api.currentUser().pipe(
      map((user) => ({ user, token }) satisfies AuthSession),
      catchError((error: unknown) => {
        this.tokens.clear();

        return throwError(() => error);
      }),
      tap((session) => {
        this.store.setUser(session.user);
        this.scheduleRenew(session.token);
      }),
    );
  }

  private discardSession(): void {
    this.renewInFlight = null;
    this.cancelScheduledRenew();
    this.tokens.clear();

    for (const key of SESSION_SCOPED_KEYS) {
      for (const kind of ['local', 'session', 'memory'] as const) {
        this.storage.remove(key, kind);
      }
    }
  }

  private scheduleRenew(token: AuthToken): void {
    this.cancelScheduledRenew();

    if (token.expiresAt === null) {
      return;
    }

    const delay = token.expiresAt - Date.now() - RENEW_LEAD_MS;
    const clamped = Math.min(Math.max(delay, 1_000), MAX_TIMEOUT_MS);

    this.renewTimer = setTimeout(() => {
      this.renewTimer = null;

      if (delay > clamped) {
        this.scheduleRenew(token);

        return;
      }

      this.renew().subscribe({ error: () => undefined });
    }, clamped);
  }

  private cancelScheduledRenew(): void {
    if (this.renewTimer !== null) {
      clearTimeout(this.renewTimer);
      this.renewTimer = null;
    }
  }
}
